import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Flex, Heading, Image, Spinner, Stack, Text } from '@chakra-ui/react';
import {
  CaretRight,
  CheckCircle,
  DownloadSimple,
  File as FileIcon,
  UploadSimple,
  Warning,
  X as XIcon
} from '@phosphor-icons/react';

import {
  apiCheckVeteransFeatureEnabled,
  apiJobBoardTranslateVeteransResume,
  CheckVeteransFeatureEnabledResponse,
  JobBoardTranslateVeteransResumeProgress
} from '@/api/services/job_board.service';
import { fetchAPI } from '@/api/fetch';
import Resume from '@/components/data-display/Resume';
import { Avatar } from '@/components/ui/avatar';
import { exportResumeToDocx, getFileSize } from '@/utils/file';
import jobBoardBanner from '@/assets/image/job-board-banner.png';
import { Agency } from '@/types/Agency';
import { getLlmModelProviderSupportFileTypes } from '@/types/enums';
import { MAX_RESUME_FILE_SIZE, MAX_RESUME_FILE_SIZE_MB } from '@/config/constants';

const FILE_SIZE_LIMIT = MAX_RESUME_FILE_SIZE;
const SUPPORTED_FILE_TYPES = getLlmModelProviderSupportFileTypes();

export function VeteransResumeTranslatorIndex() {
  const { agencyId } = useParams<{ agencyId: string }>();

  const [isCheckingFeature, setIsCheckingFeature] = useState(true);
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [featureError, setFeatureError] = useState('');
  const [agency, setAgency] = useState<Agency | null>(null);

  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const [isTranslating, setIsTranslating] = useState(false);
  const [translateProgress, setTranslateProgress] =
    useState<JobBoardTranslateVeteransResumeProgress | null>(null);
  const [translateError, setTranslateError] = useState<string | null>(null);

  const [translatedResume, setTranslatedResume] = useState<Resume | null>(null);

  const resumeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkFeature = async () => {
      if (!agencyId) {
        setFeatureError('Invalid page URL. Please check the link and try again.');
        setIsCheckingFeature(false);
        return;
      }

      try {
        const response = await fetchAPI<CheckVeteransFeatureEnabledResponse>({
          request: () => apiCheckVeteransFeatureEnabled({ agency_id: agencyId })
        });

        if (response.success && response.data?.enabled) {
          setFeatureEnabled(true);
          setAgency(response.data.agency);
        } else {
          setFeatureError(
            'This feature is not available. Please contact the agency for assistance.'
          );
        }
      } catch {
        setFeatureError('Failed to load page. Please try again later.');
      } finally {
        setIsCheckingFeature(false);
      }
    };

    checkFeature();
  }, [agencyId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
    if (!SUPPORTED_FILE_TYPES.includes(ext)) {
      const label = SUPPORTED_FILE_TYPES.length === 1 ? 'format' : 'formats';
      setFileError(`Unsupported file type. Supported ${label}: ${SUPPORTED_FILE_TYPES.join(', ')}`);
      return;
    }

    if (file.size > FILE_SIZE_LIMIT) {
      setFileError(`File size exceeds ${MAX_RESUME_FILE_SIZE_MB} MB`);
      return;
    }

    setFileError(null);
    setResumeFile(file);
    setTranslatedResume(null);
    setTranslateError(null);
  };

  const removeFile = () => {
    setResumeFile(null);
    setFileError(null);
    setTranslatedResume(null);
    setTranslateError(null);
  };

  const handleTranslate = async () => {
    if (!resumeFile) {
      setFileError('Please upload your military resume');
      return;
    }

    if (!agencyId) return;

    setIsTranslating(true);
    setTranslateError(null);
    setTranslateProgress(null);
    setTranslatedResume(null);

    try {
      const response = await apiJobBoardTranslateVeteransResume({
        agencyId,
        payload: { resume: resumeFile },
        onProgress: (p) => setTranslateProgress(p)
      });

      const isSuccess = response.code === 200 || response.code === 201;
      if (isSuccess && response.data) {
        setTranslatedResume(response.data);
      } else {
        setTranslateError('Failed to translate resume. Please try again.');
      }
    } catch {
      setTranslateError('Failed to translate resume. Please try again.');
    } finally {
      setIsTranslating(false);
      setTranslateProgress(null);
    }
  };

  const handleDownload = () => {
    if (!translatedResume) return;
    const agencyName = agency?.name ?? 'Translated';
    const fileName = translatedResume.personal_information.name
      ? `${agencyName}_${translatedResume.personal_information.name}.docx`
      : `${agencyName}_Resume.docx`;
    exportResumeToDocx(translatedResume, fileName);
  };

  if (isCheckingFeature) {
    return (
      <Flex justify="center" align="center" minH="100vh" bg="background.primary">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (!featureEnabled) {
    return (
      <Flex bg="background.primary" minH="100vh" w="100%">
        <Box p="24px" maxW="800px" mx="auto" bg="background.primary">
          <Flex
            direction="column"
            align="center"
            justify="center"
            minH="60vh"
            textAlign="center"
            gap="24px"
          >
            <Box color="error.700" display="flex" alignItems="center" justifyContent="center">
              <Warning size={60} />
            </Box>
            <Heading size="xl" color="neutrals.800">
              Feature Not Available
            </Heading>
            <Text fontSize="lg" color="neutrals.600" maxW="800px">
              {featureError}
            </Text>
            <Text fontSize="14px" color="neutrals.500" mt="8px">
              Please contact support if this issue persists.
            </Text>
          </Flex>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex bg="background.primary" minH="100vh" w="100%" direction="column">
      <Flex direction="column" maxW="1200px" mx="auto" w="100%">
        {/* Banner */}
        <Box w="100%" position="relative" overflow="hidden">
          <Image src={jobBoardBanner} alt="Banner" w="100%" h="320px" objectFit="cover" />
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            pointerEvents="none"
            bgGradient="linear(to-r, background.primary 0%, transparent 10%, transparent 90%, background.primary 100%)"
          />
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            pointerEvents="none"
            bgGradient="linear(to-b, background.primary 0%, transparent 10%, transparent 90%, background.primary 100%)"
          />
        </Box>

        <Stack p="32px 120px 64px 120px" w="100%" gap="32px">
          {/* Agency info */}
          <Flex justify="space-between" align="flex-start" mb="80px">
            <Box mb="48px">
              <Avatar
                size="2xl"
                w="80px"
                h="80px"
                src={agency?.logo_url}
                name={agency?.name}
                bg="primary.900"
                color={agency?.logo_url ? 'transparent' : 'neutrals.0'}
                fontSize="24px"
                mb="16px"
              />
              <Heading fontSize="32px">{agency?.name}</Heading>
            </Box>
            <a
              href={`/job_board/${agencyId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <Flex
                align="center"
                gap="8px"
                px="12px"
                py="8px"
                color="primary.900"
                cursor="pointer"
                _hover={{ textDecoration: 'underline' }}
                transition="all 0.2s"
                flexShrink={0}
              >
                <Text fontSize="14px" fontWeight="medium" color="primary.900">
                  Job Board
                </Text>
                <CaretRight size={16} weight="bold" />
              </Flex>
            </a>
          </Flex>

          <Box>
            <Heading fontSize="32px" mb="8px">
              Military Resume Translator
            </Heading>
            <Text fontSize="16px" color="neutrals.600">
              Upload your military resume and we'll translate it into a civilian-friendly format.
            </Text>
          </Box>

          {/* Upload Section */}
          <Box>
            <Text fontSize="16px" fontWeight="medium" mb="8px" color="neutrals.700">
              Military Resume
            </Text>
            <Text fontSize="14px" color="neutrals.500" mb="12px">
              Supported {SUPPORTED_FILE_TYPES.length === 1 ? 'format' : 'formats'}:{' '}
              {SUPPORTED_FILE_TYPES.join(', ')} ({getFileSize(FILE_SIZE_LIMIT)} max)
            </Text>

            {resumeFile ? (
              <Box display="flex" alignItems="center" width="100%">
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  width="120px"
                  height="120px"
                  borderRadius="12px"
                  border="1px solid"
                  borderColor="primary.100"
                  bg="primary.50"
                >
                  <FileIcon size={48} color="#4e76ff" />
                </Box>
                <Box ml="24px" color="neutrals.900" flex="1">
                  <Box display="flex" alignItems="center" mb="4px">
                    <Box mr="12px">
                      <CheckCircle size={20} color="#4e76ff" weight="fill" />
                    </Box>
                    <Text fontSize="16px" color="neutrals.800" mr="12px">
                      {resumeFile.name}
                    </Text>
                    {!isTranslating && (
                      <Box cursor="pointer" onClick={removeFile}>
                        <XIcon size={16} color="#545454" />
                      </Box>
                    )}
                  </Box>
                  <Text fontSize="14px" color="neutrals.500">
                    {getFileSize(resumeFile.size)}
                  </Text>
                  {fileError && (
                    <Text fontSize="14px" color="error.700" mt="4px">
                      {fileError}
                    </Text>
                  )}
                </Box>
              </Box>
            ) : (
              <Box
                position="relative"
                width="100%"
                height="160px"
                border="2px dashed"
                borderColor={fileError ? 'error.300' : 'neutrals.300'}
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                _hover={{ borderColor: fileError ? 'error.400' : 'neutrals.400' }}
                bg={fileError ? 'error.50' : 'neutrals.50'}
              >
                <input
                  type="file"
                  accept={SUPPORTED_FILE_TYPES.join(',')}
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer'
                  }}
                  onChange={handleFileChange}
                  disabled={isTranslating}
                />
                <Box display="flex" flexDirection="column" alignItems="center">
                  <UploadSimple size={48} color={fileError ? '#dc2626' : '#404040'} />
                  <Text textAlign="center" color="neutrals.900" mt="8px">
                    Drop file here or{' '}
                    <Box as="span" color={fileError ? 'error.600' : 'primary.900'}>
                      click to upload
                    </Box>
                  </Text>
                  {fileError && (
                    <Text fontSize="14px" color="error.700" mt="8px">
                      {fileError}
                    </Text>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Progress */}
          {isTranslating && (
            <Box>
              <Flex align="center" gap="8px" mb="12px">
                <Box color="warning.500">
                  <Warning size={18} weight="fill" />
                </Box>
                <Text fontSize="14px" color="warning.500">
                  Please don't refresh the page. This translation typically takes 30 seconds.
                </Text>
              </Flex>
              <Flex justify="space-between" mb="6px">
                <Text fontSize="14px" color="neutrals.500">
                  {translateProgress?.step ?? 'Translating military resume...'}
                </Text>
                <Text fontSize="14px" fontWeight="semibold" color="primary.900">
                  {translateProgress?.percentage ?? 0}%
                </Text>
              </Flex>
              <Box w="100%" h="8px" bg="neutrals.200" borderRadius="full" overflow="hidden">
                <Box
                  h="100%"
                  bg="primary.900"
                  borderRadius="full"
                  w={`${translateProgress?.percentage ?? 0}%`}
                  transition="width 0.3s ease"
                />
              </Box>
            </Box>
          )}

          {/* Error */}
          {translateError && (
            <Text fontSize="16px" color="error.700">
              {translateError}
            </Text>
          )}

          {/* Translate Button */}
          {!translatedResume && (
            <Button
              alignSelf="center"
              p="24px 48px"
              size="lg"
              bg="primary.900"
              color="white"
              borderRadius="8px"
              fontSize="16px"
              fontWeight="medium"
              loading={isTranslating}
              disabled={isTranslating || !resumeFile}
              _hover={{ bg: 'primary.700' }}
              onClick={handleTranslate}
            >
              Translate
            </Button>
          )}

          {/* Resume Preview */}
          {translatedResume && (
            <Box>
              <Box
                border="1px solid"
                borderColor="success.500"
                borderRadius="8px"
                p="16px"
                mb="20px"
              >
                <Flex align="center" gap="12px">
                  <Box color="success.500">
                    <CheckCircle size={18} weight="fill" />
                  </Box>
                  <Text fontSize="14px" color="success.500" fontWeight="semibold">
                    Success! Your military experience has been seamlessly translated into civilian
                    language. Review your new resume below and download it to customize and make it
                    uniquely yours.
                  </Text>
                </Flex>
              </Box>
              <Flex justify="center" mt="20px" mb="20px">
                <Button
                  p="12px 24px"
                  size="lg"
                  bg="primary.900"
                  color="neutrals.0"
                  borderRadius="8px"
                  fontSize="16px"
                  fontWeight="medium"
                  _hover={{ bg: 'primary.500' }}
                  onClick={handleDownload}
                >
                  <DownloadSimple size={16} />
                  Download Resume
                </Button>
              </Flex>
              <Heading fontSize="24px" mb="20px">
                Translated Resume
              </Heading>
              <Resume ref={resumeRef} resume={translatedResume} />
              <Flex justify="center" mt="20px">
                <Button
                  p="12px 24px"
                  size="lg"
                  bg="primary.900"
                  color="neutrals.0"
                  borderRadius="8px"
                  fontSize="16px"
                  fontWeight="medium"
                  _hover={{ bg: 'primary.500' }}
                  onClick={handleDownload}
                >
                  <DownloadSimple size={16} />
                  Download Resume
                </Button>
              </Flex>
            </Box>
          )}
        </Stack>

        <Box as="footer" bg="background.primary" h="40px" alignContent="center">
          <Text fontSize="14px" color="neutrals.700" textAlign="center">
            Powered by{' '}
            <Text as="span" color="primary.900" fontWeight="bold">
              <a href="https://www.polarisjobs.com" target="_blank" rel="noreferrer">
                PolarisJobs
              </a>
            </Text>
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
}

export default VeteransResumeTranslatorIndex;
